// Hook (use-auth.js)
import React, { useState, useContext, createContext, useEffect } from "react";
import { UserVehicleApi } from "../apis/UserVehicleApi";
import { UserWalletApi } from "../apis/UserWallet";
import { UserSettingsApi } from "../apis/UserSettingsApi";
import { UserPropertiesApi } from "../apis/UserPropertiesApi";
import { UserDashboardSessionsApi } from "../apis/UserDashboardSessionsApi";
import { useAuth } from "../auth/Auth";
import { DevicesApi } from '../apis/DevicesApi'
import { SmartOutletsApi } from '../apis/SmartOutletsApi'

const dataContext = createContext({
    userWallet: {},
});
// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideData({ children }) {
    const data = useProvideData();
    return <dataContext.Provider value={data}>{children}</dataContext.Provider>;
}
// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useData = () => {
    return useContext(dataContext);
};
// Provider hook that creates auth object and handles state
function useProvideData() {
    const auth = useAuth()
    const [initComplete, setInitComplete] = useState(false);
    const [isSetupComplete, setIsSetupComplete] = useState(null);
    const [userVehicles, setUserVehicles] = useState(null);
    const [userWallet, setUserWallet] = useState(null);
    const [userSettings, setUserSettings] = useState(null);
    const [userProperties, setUserProperties] = useState([])
    const [guestChargers, setGuestChargers] = useState([])
    const [smartOutlets, setSmartOutlets] = useState([])
    const [userDashboardSessions, setUserDashboardSessions] = useState(null)


    const loadUserVehicles = () => {
        UserVehicleApi.get().then(data => {
            setUserVehicles(data.userVehicles)
        }).catch(error => { })
    }

    const loadGuestChargers = () => {
        DevicesApi.getGuestChargers().then(data => {
            setGuestChargers(data)
        }).catch(error => { })
    }

    const loadSmartOutlets = (propertyUuid) => {
        if (!propertyUuid) return
        SmartOutletsApi.getByProperty(propertyUuid).then(data => {
            setSmartOutlets(data.smartOutlets || [])
        }).catch(() => { })
    }

    const loadUserWallet = () => {
        UserWalletApi.get().then(data => {
            setUserWallet(data)
        }).catch(error => { })
    }

    const loadUserSettings = () => {
        UserSettingsApi.get().then(data => {
            setUserSettings(data)
            setInitComplete(true)
            setIsSetupComplete(data.initialSetupComplete)
        }).catch(error => { })
    }

    const loadUserProperties = () => {
        UserPropertiesApi.get().then(data => {
            setUserProperties(data)
            if (Array.isArray(data) && data.length > 0) {
                loadSmartOutlets(data[0].uuid)
            }
        }).catch(error => { })
    }

    const loadDashboardSessionData = () => {
        UserDashboardSessionsApi.get().then(data => {
            setUserDashboardSessions(data)
        }).catch(error => { })
    }

    const loadInitialData = () => {
        if (auth.isInstaller) {
            setInitComplete(true)
        } else {
            loadUserVehicles()
            loadUserWallet()
            loadUserSettings()
            loadUserProperties()
            loadDashboardSessionData()
            loadGuestChargers()
        }
    }

    useEffect(() => {
        if (auth.isAuthenticated && auth.isInstaller !== null) {
            loadInitialData()
        }
    }, [auth.isInstaller, auth.isAuthenticated])

    // Return the user object and auth methods
    return {
        initComplete,
        userVehicles,
        userWallet,
        userSettings,
        userProperties,
        guestChargers,
        smartOutlets,
        isSetupComplete,
        setIsSetupComplete,
        userDashboardSessions,
        setUserDashboardSessions,
        loadInitialData,
        loadUserProperties,
        loadDashboardSessionData,
        loadUserWallet,
    };
}